import { useState } from "react";
import axios from "axios";
import { Building2, Sparkles, Puzzle } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { getSolutionIcon } from "@/lib/solutionIcons";
import { centsToDisplay } from "@/lib/money";
import { useSaveStatus } from "./useSaveStatus";
import { inputClass, labelClass, saveBtnClass } from "./orgFormStyles";
import OrgSolutionCapabilitiesPanel from "./OrgSolutionCapabilitiesPanel";

const SOLUTION_ITEM_STATUSES = ["Purchased", "Setup Required", "Configured", "Active"];

export default function OrgSolutionsTab({ org, catalog, API, onSaved, onGoToOrgSetup }) {
  const [managing, setManaging] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [toggleError, setToggleError] = useState("");

  const toggleSolution = async (solution) => {
    setTogglingId(solution.solution_id);
    setToggleError("");
    try {
      await axios.patch(`${API}/admin/solutions/${solution.solution_id}/organizations/${org.org_id}`, { enabled: !solution.enabled }, { withCredentials: true });
      await onSaved();
    } catch (err) {
      setToggleError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTogglingId(null);
    }
  };

  const managingSolution = catalog.find((s) => s.solution_id === managing);

  return (
    <div className="space-y-6" data-testid="org-solutions-tab">
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm dark:border-white/10 dark:bg-[#111C33]" data-testid="org-solutions-industry-badge">
          <Building2 size={16} className="text-slate-400 dark:text-slate-500" />
          <span className="font-semibold text-slate-700 dark:text-slate-300">Industry: {org.industry || "Not set"}</span>
          <button type="button" onClick={onGoToOrgSetup} data-testid="org-solutions-change-industry" className="ml-1 text-xs font-bold text-blue-600 hover:underline dark:text-blue-400">Change Industry</button>
        </div>
        {org.industry && (
          <div className="flex flex-1 min-w-[280px] items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm dark:border-violet-500/20 dark:bg-violet-500/10" data-testid="org-solutions-recommendation-banner">
            <Sparkles size={16} className="shrink-0 text-violet-600 dark:text-violet-400" />
            <p className="text-violet-800 dark:text-violet-300">
              <span className="font-bold">Using industry recommendations. </span>
              Recommended capabilities for {org.industry} have been pre-selected. You can adjust as needed.
            </p>
          </div>
        )}
      </div>

      <Panel title="Purchased Plan & Add-ons" subtitle="From the accepted quote. For reference only.">
        {(org.solution_items || []).length === 0 ? (
          <EmptyState icon={Puzzle} title="No purchased plan items." message="Plan items carry forward automatically from the accepted quote." testId="org-purchased-plan-empty" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="org-purchased-plan-list">
            {org.solution_items.map((item) => (
              <div key={item.item_id} className="rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid={`org-purchased-plan-item-${item.item_id}`}>
                <p className="text-sm font-bold text-slate-950 dark:text-white">{item.description}</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Qty {item.quantity} · {centsToDisplay(item.unit_price_cents)}{item.billing_type === "one_time" ? " one-time" : "/month"}</p>
                <span className="mt-2 inline-block rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">{item.status}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Nova Solutions" subtitle="Enable or disable Nova solutions for this organization. Click Manage to configure capabilities.">
        {toggleError && <p className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400">{toggleError}</p>}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="org-nova-solutions-grid">
          {catalog.map((s) => {
            const Icon = getSolutionIcon(s.icon);
            return (
              <div key={s.solution_id} className="rounded-2xl border border-slate-200 p-5 dark:border-white/10" data-testid={`org-nova-solution-card-${s.slug}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-500/10"><Icon size={20} className="text-[#0052FF] dark:text-blue-400" /></span>
                  <button
                    type="button"
                    onClick={() => toggleSolution(s)}
                    disabled={togglingId === s.solution_id}
                    data-testid={`org-nova-solution-toggle-${s.slug}`}
                    className={`h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ${s.enabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-white/15"}`}
                  >
                    <span className={`block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${s.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                </div>
                <p className="mt-3 text-sm font-bold text-slate-950 dark:text-white">{s.name}</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{s.description}</p>
                <button
                  type="button"
                  onClick={() => setManaging(managing === s.solution_id ? null : s.solution_id)}
                  data-testid={`org-nova-solution-manage-${s.slug}`}
                  className="mt-3 text-xs font-bold text-blue-600 hover:underline dark:text-blue-400"
                >
                  {managing === s.solution_id ? "Close" : "Manage"} →
                </button>
              </div>
            );
          })}
        </div>
      </Panel>

      {managingSolution && (
        <OrgSolutionCapabilitiesPanel org={org} solution={managingSolution} API={API} onSaved={onSaved} />
      )}

      <PlatformConfigurationPanel org={org} API={API} onSaved={onSaved} />
    </div>
  );
}

const PlatformConfigurationPanel = ({ org, API, onSaved }) => {
  const [platformTier, setPlatformTier] = useState(org.platform_tier || "");
  const [supportLevel, setSupportLevel] = useState(org.support_level || "");
  const [effectiveDate, setEffectiveDate] = useState(org.effective_date ? org.effective_date.slice(0, 10) : "");
  const [items, setItems] = useState(org.solution_items || []);
  const { saving, setSaving, error, setError, savedFlash, flash } = useSaveStatus();

  const setItemStatus = (itemId, status) => setItems((list) => list.map((i) => (i.item_id === itemId ? { ...i, status } : i)));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.patch(
        `${API}/admin/organizations/${org.org_id}/solutions`,
        { platform_tier: platformTier, support_level: supportLevel, effective_date: effectiveDate || null, solution_items: items.map((i) => ({ item_id: i.item_id, status: i.status })) },
        { withCredentials: true }
      );
      await onSaved();
      flash();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Platform Configuration" subtitle="Internal reference details for this organization's plan.">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className={labelClass}>Platform Tier</label>
          <input value={platformTier} onChange={(e) => setPlatformTier(e.target.value)} data-testid="org-solutions-tier" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Dedicated Support Level</label>
          <input value={supportLevel} onChange={(e) => setSupportLevel(e.target.value)} data-testid="org-solutions-support-level" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Effective / Start Date</label>
          <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="org-solutions-effective-date" className={inputClass} />
        </div>
      </div>
      {items.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="org-solutions-item-status-list">
          {items.map((item) => (
            <div key={item.item_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 dark:border-white/10">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.description}</p>
              <select value={item.status} onChange={(e) => setItemStatus(item.item_id, e.target.value)} data-testid={`org-solution-item-status-${item.item_id}`} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
                {SOLUTION_ITEM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400">{error}</p>}
      <div className="mt-4 flex items-center gap-4">
        <button type="button" onClick={save} disabled={saving} data-testid="org-solutions-save-btn" className={saveBtnClass}>{saving ? "Saving…" : "Save"}</button>
        {savedFlash && <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Saved</span>}
      </div>
    </Panel>
  );
};
